// src/pages/admin/CourseLectures/lectures/Lecture08AusbildungSuche.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${isOpen ? "border-[#009fe3]/50" : "border-gray-200"} bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"}`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${isOpen ? "text-[#0369a1]" : "text-gray-800"}`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Portal Card ───────────────────────────────────────────────────
const PortalCard: React.FC<{
  name: string;
  badge: string;
  badgeColor: string;
  what: string;
  target: string;
  advantage: string;
  tip: string;
  link?: string;
  linkLabel?: string;
  warning?: string;
  hack?: string;
  color: string;
}> = ({
  name,
  badge,
  badgeColor,
  what,
  target,
  advantage,
  tip,
  link,
  linkLabel,
  warning,
  hack,
  color,
}) => (
  <div className={`rounded-xl border p-4 ${color}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-bold">{name}</span>
      <span
        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeColor}`}
      >
        {badge}
      </span>
    </div>
    <div className="space-y-1.5 text-xs">
      <div className="flex gap-2">
        <span className="font-bold flex-shrink-0 w-20 opacity-60">
          Was ist es?
        </span>
        <span>{what}</span>
      </div>
      <div className="flex gap-2">
        <span className="font-bold flex-shrink-0 w-20 opacity-60">
          Zielgruppe
        </span>
        <span>{target}</span>
      </div>
      <div className="flex gap-2">
        <span className="font-bold flex-shrink-0 w-20 opacity-60">Vorteil</span>
        <span>{advantage}</span>
      </div>
      <div className="flex gap-2">
        <span className="font-bold flex-shrink-0 w-20 opacity-60">Tipp</span>
        <span>{tip}</span>
      </div>
      {warning && (
        <div className="flex gap-2">
          <span className="font-bold flex-shrink-0 w-20 opacity-60">
            Wichtig
          </span>
          <span className="font-semibold">{warning}</span>
        </div>
      )}
      {hack && (
        <div className="flex gap-2">
          <span className="font-bold flex-shrink-0 w-20 opacity-60">Hack</span>
          <span className="italic">{hack}</span>
        </div>
      )}
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-1.5 font-bold underline opacity-80 hover:opacity-100"
        >
          🔗 {linkLabel || link}
        </a>
      )}
    </div>
  </div>
);

// ── Script Block ──────────────────────────────────────────────────
const ScriptBlock: React.FC<{
  title: string;
  lines: { label?: string; text: string; highlight?: boolean }[];
}> = ({ title, lines }) => (
  <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono">
    <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-3">
      {title}
    </p>
    <div className="space-y-2">
      {lines.map((line, i) => (
        <div key={i}>
          {line.label && (
            <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">
              {line.label}
            </p>
          )}
          <p className={line.highlight ? "text-amber-300" : "text-gray-300"}>
            {line.text}
          </p>
        </div>
      ))}
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const Lecture08AusbildungSuche: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Aufgaben aus Lektion 07 — erledigt?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Bewerbungen im Tracker protokolliert",
            "Mindestens 2 Bewerbungen täglich eingereicht",
            "Fokus auf B2-Sprachniveau & Sprachzeugnis",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Challenge Banner */}
      <div className="bg-gray-900 rounded-xl px-4 py-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            {
              label: "Der Traum",
              text: "Eine Ausbildung in Deutschland (Duale Berufsausbildung)",
              icon: "🎯",
              color: "text-[#009fe3]",
            },
            {
              label: "Das Problem",
              text: "Einen Arbeitgeber aus dem Ausland zu finden gleicht der Suche nach der Nadel im Heuhaufen",
              icon: "🪡",
              color: "text-red-400",
            },
            {
              label: "Die Realität",
              text: "Viele Bewerbungen, keine Antworten?",
              icon: "📭",
              color: "text-amber-400",
            },
            {
              label: "Die Lösung",
              text: "Gezielte Suchstrategien statt Massenbewerbungen",
              icon: "✅",
              color: "text-green-400",
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-lg flex-shrink-0">{item.icon}</span>
              <div>
                <p
                  className={`text-xs font-bold uppercase tracking-wide ${item.color}`}
                >
                  {item.label}
                </p>
                <p className="text-xs text-gray-300 mt-0.5">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Suchportale — Make it in Germany & IHK" },
          { num: "2", label: "Suchportale — Ausbildung.de & Bundesagentur" },
          {
            num: "3",
            label: "Suchportale — Azubi.de & Big-Player-Karriereseiten",
          },
          { num: "4", label: "Geheimtipp — Gelbe Seiten & Google Maps" },
          { num: "5", label: "Keine Stellenanzeige? — Strategie & Anruf" },
          {
            num: "6",
            label: "Keine Stellenanzeige? — Vorabanfrage per E-Mail",
          },
          { num: "7", label: "Portal-Sprung — Direkt zur Quelle" },
          { num: "8", label: "Die Kunst des Nachfassens — Follow-Up" },
          { num: "9", label: "Mindset — Qualität vor Quantität" },
        ]}
      />

      {/* 01 — Make it in Germany + IHK */}
      <Accordion
        id="01"
        num="ABSCHNITT 01"
        icon="🌍"
        title="Suchportale — Make it in Germany & IHK Lehrstellenbörse"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Die besten Startpunkte für internationale Bewerber — beide sind
          offiziell und speziell auf ausländische Kandidaten ausgerichtet.
        </p>
        <div className="space-y-3">
          <PortalCard
            name="🇩🇪 Make it in Germany"
            badge="Empfohlen für Ausländer"
            badgeColor="bg-[#009fe3] text-white"
            what="Das offizielle Portal der Bundesregierung"
            target="Speziell für ausländische Fachkräfte & Azubis"
            advantage="Alle Angebote geprüft — Arbeitgeber sind offen für internationale Bewerber"
            tip='Filter "Art des Jobangebots" → "Ausbildungsplatz" setzen'
            link="https://www.make-it-in-germany.com/de/arbeiten-in-deutschland/jobboerse"
            linkLabel="make-it-in-germany.com → Jobbörse"
            color="bg-blue-50 border-blue-200 text-blue-800"
          />
          <PortalCard
            name="🏛️ IHK Lehrstellenbörse"
            badge="Verifizierte Stellen"
            badgeColor="bg-purple-100 text-purple-800 border border-purple-200"
            what="Jobbörse der Industrie- und Handelskammern (IHK)"
            target="IHK überwacht Qualität der dualen Ausbildung"
            advantage="Tausende verifizierte Ausbildungsplätze"
            tip='Nach "Beruf" suchen (z.B. Koch, Pflegefachmann)'
            warning="Keine Region einschränken — Deutschlandweit suchen!"
            link="https://meine-ausbildung-in-deutschland.de/"
            linkLabel="meine-ausbildung-in-deutschland.de"
            color="bg-purple-50 border-purple-200 text-purple-800"
          />
        </div>
      </Accordion>

      {/* 02 — Ausbildung.de + Bundesagentur */}
      <Accordion
        id="02"
        num="ABSCHNITT 02"
        icon="🔍"
        title="Suchportale — Ausbildung.de & Bundesagentur für Arbeit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3">
          <PortalCard
            name="📚 Ausbildung.de"
            badge="Beliebtestes Portal"
            badgeColor="bg-green-100 text-green-800 border border-green-200"
            what="Das beliebteste Portal bei deutschen Schülern"
            target="Breites Publikum — viele Firmenprofile"
            advantage="Detaillierte Firmenprofile und Kultur-Beschreibungen"
            tip='Profil erstellen. Filter "Beginn" beachten — Bewerbungen oft 1 Jahr im Voraus!'
            hack="Informationen zur Unternehmenskultur für dein Anschreiben nutzen"
            color="bg-green-50 border-green-200 text-green-800"
          />
          <PortalCard
            name="🏢 Bundesagentur für Arbeit"
            badge="Größte staatliche Jobbörse"
            badgeColor="bg-amber-100 text-amber-800 border border-amber-200"
            what="Die größte staatliche Jobbörse Deutschlands"
            target="Alle Unternehmen — besonders KMU (kleine & mittlere Unternehmen)"
            advantage='KMUs sind oft "Hidden Gems" für Ausländer — weniger Konkurrenz'
            tip='Filter "Sie suchen" → "Ausbildung" setzen. Navigation etwas komplex.'
            link="https://www.arbeitsagentur.de/jobsuche/"
            linkLabel="arbeitsagentur.de/jobsuche"
            color="bg-amber-50 border-amber-200 text-amber-800"
          />
        </div>
        <InfoBox variant="tip" icon="💡" title="KMU-Strategie">
          Kleine und mittlere Unternehmen erhalten weniger internationale
          Bewerbungen — deine Chancen sind hier oft{" "}
          <strong>deutlich höher</strong> als bei großen Konzernen.
        </InfoBox>
      </Accordion>

      {/* 03 — Azubi.de + Big Players */}
      <Accordion
        id="03"
        num="ABSCHNITT 03"
        icon="🏆"
        title="Suchportale — Azubi.de & Karriereseiten der Big Players"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3 mb-4">
          <PortalCard
            name="🎓 Azubi.de"
            badge="Ergänzungsportal"
            badgeColor="bg-orange-100 text-orange-800 border border-orange-200"
            what="Ein weiteres nützliches Portal zur Ergänzung"
            target="Breites Spektrum an Ausbildungsberufen"
            advantage="Gute Ergänzung zu den anderen Portalen"
            tip='"Job Alert" aktivieren — automatisch benachrichtigt werden bei neuen Stellen'
            color="bg-orange-50 border-orange-200 text-orange-800"
          />
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🏭</span>
              <span className="text-sm font-bold text-red-800">
                Karriereseiten der "Big Players"
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                Nur eigene Portale!
              </span>
            </div>
            <div className="space-y-1.5 text-xs text-red-700">
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">›</span>
                <span>
                  Große Firmen (Siemens, BMW, Deutsche Telekom etc.) nutzen{" "}
                  <strong>oft nur ihre eigenen Karriereportale</strong> — sie
                  sind nicht auf ausbildung.de oder ähnlichen Portalen
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">›</span>
                <span>
                  Suche:{" "}
                  <code className="bg-white px-1 rounded border border-red-200">
                    [Firmenname] Ausbildung Karriereseite
                  </code>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-0.5">›</span>
                <span>
                  "Job Alert" aktivieren — Frühzeitig informiert sein ist
                  entscheidend
                </span>
              </div>
            </div>
            <div className="mt-2 bg-white rounded-lg px-3 py-2 border border-red-200">
              <p className="text-[10px] text-red-600 font-bold uppercase tracking-wide mb-0.5">
                Timing-Warnung:
              </p>
              <p className="text-xs text-red-700">
                Große Firmen rekrutieren <strong>sehr früh</strong> — Deadlines
                beachten! Oft 1 Jahr im Voraus.
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* 04 — Gelbe Seiten + Google Maps */}
      <Accordion
        id="04"
        num="ABSCHNITT 04"
        icon="🗺️"
        title="Geheimtipps — Gelbe Seiten & Google Maps"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Zwei unterschätzte Quellen, die kaum ein anderer Bewerber nutzt — dein
          Wettbewerbsvorteil.
        </p>
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📒</span>
              <span className="text-sm font-bold text-yellow-800">
                Gelbe Seiten — Das Telefonbuch
              </span>
            </div>
            <div className="space-y-1.5 text-xs text-yellow-800">
              <div className="flex gap-2">
                <span className="font-bold flex-shrink-0 w-24 opacity-60">
                  Quelle
                </span>
                <span>www.gelbeseiten.de (Branchenverzeichnis)</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold flex-shrink-0 w-24 opacity-60">
                  Zielgruppe
                </span>
                <span>Traditionelle Firmen, die online wenig aktiv sind</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold flex-shrink-0 w-24 opacity-60">
                  Strategie
                </span>
                <span>Was: Beruf (z.B. Altenpflege) — Wo: Beliebige Stadt</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold flex-shrink-0 w-24 opacity-60">
                  Aktion
                </span>
                <span className="font-semibold">
                  E-Mail und Telefonnummern sammeln → Direkt kontaktieren
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">🗺️</span>
                <span className="text-sm font-bold text-[#0369a1]">
                  Google Maps — Der Geheimtipp
                </span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#009fe3] text-white">
                90% nutzen das nicht!
              </span>
            </div>
            <p className="text-xs text-[#0369a1] mb-3">
              90% der Bewerber nutzen dies nicht — dein exklusiver Vorteil.
            </p>
            <div className="space-y-2">
              {[
                {
                  step: "1",
                  text: "Stadt in Deutschland auf der Karte auswählen (z.B. Frankfurt) & heranzoomen",
                },
                {
                  step: "2",
                  text: 'Branche suchen (z.B. "Elektro Firma", "Hotel", "IT Firma")',
                },
                {
                  step: "3",
                  text: 'Websites der Ergebnisse anklicken → "Karriere/Jobs" prüfen',
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="flex items-start gap-3 bg-white/60 rounded-lg px-3 py-2 border border-[#009fe3]/20"
                >
                  <div className="w-5 h-5 rounded-full bg-[#009fe3] flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                    {item.step}
                  </div>
                  <p className="text-xs text-[#0369a1]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <InfoBox variant="tip" icon="🏆" title="Warum das funktioniert">
          Firmen auf Google Maps haben oft keine aktiven Stellenanzeigen — aber
          sie bilden trotzdem aus. Ein direkter Kontakt fällt auf und zeigt
          Initiative.
        </InfoBox>
      </Accordion>

      {/* 05 — Keine Stellenanzeige: Strategie + Anruf */}
      <Accordion
        id="05"
        num="ABSCHNITT 05"
        icon="📞"
        title="Keine Stellenanzeige? — Strategie & Der Anruf (Königsweg)"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Keine offene Stelle bedeutet nicht: kein Ausbildungsplatz. So gehst du
          vor, wenn die Karriereseite leer ist.
        </p>
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-bold text-blue-800 mb-2">
              🔎 Die Strategie in 3 Schritten
            </p>
            <div className="space-y-2">
              {[
                {
                  icon: "🔍",
                  label: "Recherche",
                  desc: 'Finde heraus, ob das Unternehmen generell ausbildet — Website "Über uns", Social Media',
                },
                {
                  icon: "📞",
                  label: "Telefonischer Vorstoß (Empfohlen)",
                  desc: '"Bilden Sie dieses Jahr im Bereich XY aus? Darf ich Ihnen meine Unterlagen senden?"',
                  highlight: true,
                },
                {
                  icon: "✉️",
                  label: "Schriftliche Ansprache",
                  desc: "Zeige Motivation durch Wissen über die Firma — persönlich und individuell",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-3 rounded-lg px-3 py-2 border ${item.highlight ? "bg-green-50 border-green-200" : "bg-white border-blue-100"}`}
                >
                  <span className="text-base flex-shrink-0">{item.icon}</span>
                  <div>
                    <p
                      className={`text-xs font-bold ${item.highlight ? "text-green-800" : "text-blue-800"}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${item.highlight ? "text-green-700 italic" : "text-blue-600 opacity-80"}`}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-3">
          <p className="text-xs font-bold text-amber-800 mb-1">
            🥇 Warum Anrufen der Königsweg ist
          </p>
          <p className="text-xs text-amber-700">
            Ein Anruf ist oft besser als eine E-Mail — man bekommt direkt eine
            Antwort und kann sich in der Bewerbung auf das Telefonat beziehen (
            <em>"Wie besprochen..."</em>). Das wirkt sehr professionell.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {["Skype", "Mobilvoip", "Brilliant App"].map((app) => (
              <span
                key={app}
                className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-800"
              >
                {app}
              </span>
            ))}
          </div>
        </div>

        <ScriptBlock
          title="Gesprächsleitfaden — Schritt für Schritt"
          lines={[
            {
              label: "1. Zentrale anrufen:",
              text: '"Guten Tag, mein Name ist [Name]. Könnten Sie mich bitte mit der Personalabteilung oder dem Ausbildungsleiter verbinden?"',
            },
            {
              label: "2. Im Gespräch mit dem Verantwortlichen:",
              text: '"Guten Tag Frau/Herr [Name], ich habe gesehen, dass Sie ein sehr interessantes Unternehmen im Bereich [Branche] sind. Ich wollte mich erkundigen, ob Sie für dieses Jahr Auszubildende als [Beruf] suchen, auch wenn aktuell keine Anzeige online ist?"',
              highlight: true,
            },
            {
              label: "3. Wenn die Antwort JA ist:",
              text: '"Das freut mich sehr! Darf ich Ihnen meine Bewerbungsunterlagen per E-Mail schicken? An welche E-Mail-Adresse darf ich das senden?"',
            },
          ]}
        />
        <div className="mt-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <p className="text-xs font-bold text-red-800">
            ⚠️ Wichtig: Notiere dir den Namen der Person, mit der du gesprochen
            hast!
          </p>
        </div>
      </Accordion>

      {/* 06 — Vorabanfrage per E-Mail */}
      <Accordion
        id="06"
        num="ABSCHNITT 06"
        icon="✉️"
        title="Keine Stellenanzeige? — Die Vorabanfrage per E-Mail"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
            <p className="text-sm font-bold text-purple-800 mb-1">
              Was ist eine Vorabanfrage?
            </p>
            <ul className="space-y-1 text-xs text-purple-700">
              <li className="flex items-start gap-1.5">
                <span className="flex-shrink-0 mt-0.5">›</span>Noch keine
                vollständige Bewerbung — nur eine höfliche Frage, ob es sich
                lohnt
              </li>
              <li className="flex items-start gap-1.5">
                <span className="flex-shrink-0 mt-0.5">›</span>Spart dem
                Personaler Zeit und zeigt Respekt
              </li>
              <li className="flex items-start gap-1.5">
                <span className="flex-shrink-0 mt-0.5">›</span>Wenn die Antwort
                Ja ist: Du hast bereits einen persönlichen Kontakt!
              </li>
            </ul>
          </div>
        </div>
        <ScriptBlock
          title="Beispiel — Vorabanfrage E-Mail"
          lines={[
            {
              label: "Betreff:",
              text: "Frage zur Ausbildung 2026 im Bereich [Bereich]",
              highlight: true,
            },
            {
              label: "Anrede:",
              text: "Sehr geehrte Damen und Herren, (besser: Name, falls bekannt)",
            },
            {
              label: "Einleitung:",
              text: "ich verfolge die Entwicklung Ihres Unternehmens mit großem Interesse, konnte aber auf Ihrer Webseite aktuell keine offene Stelle für eine Ausbildung zum [Beruf] finden.",
            },
            {
              label: "Frage:",
              text: "Da ich sehr motiviert wäre, meine Ausbildung gerade bei Ihnen zu absolvieren, möchte ich mich kurz erkundigen: Ist es möglich, sich bei Ihnen initiativ für das kommende Ausbildungsjahr zu bewerben?",
            },
            {
              label: "Kontakt:",
              text: "Falls ja: An wen darf ich meine Unterlagen (Lebenslauf und Zeugnisse) richten?",
            },
            {
              label: "Abschluss:",
              text: "Vielen Dank für Ihre kurze Rückmeldung.\nMit freundlichen Grüßen\n[Dein Name]",
            },
          ]}
        />
      </Accordion>

      {/* 07 — Portal-Sprung */}
      <Accordion
        id="07"
        num="ABSCHNITT 07"
        icon="🚀"
        title="Portal-Sprung — Direkt zur Quelle bewerben"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Portale sind nur Zwischenhändler — für die eigentliche Bewerbung
          direkt zur Firma wechseln.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
            <p className="text-xs font-bold text-orange-800 mb-1">
              ❌ Warum Portale allein nicht reichen
            </p>
            <p className="text-xs text-orange-700">
              Portale leiten deine Bewerbung nur weiter — mit Zeitverzug und
              weniger Kontext für den Recruiter.
            </p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-800 mb-1">
              ✅ Warum direkt besser ist
            </p>
            <p className="text-xs text-green-700">
              Bewerbung landet direkt im HR-System der Firma — und du siehst oft
              zusätzliche Team-Infos für das Anschreiben.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {[
            {
              step: "1",
              icon: "📋",
              label: "Firmennamen kopieren",
              desc: "Exakten Firmennamen aus dem Portal kopieren",
              cls: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              step: "2",
              icon: "🔍",
              label: "In Suchmaschine suchen",
              desc: 'Firmennamen + "Ausbildung" oder "Karriere" googeln',
              cls: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              step: "3",
              icon: "💼",
              label: "Karriere-Reiter finden",
              desc: 'Auf der Firmenhomepage nach "Karriere" oder "Jobs" suchen und direkt bewerben',
              cls: "bg-green-50 border-green-200 text-green-800",
            },
          ].map((item) => (
            <div
              key={item.step}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${item.cls}`}
            >
              <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-extrabold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <p className="text-sm font-bold">
                  {item.icon} {item.label}
                </p>
                <p className="text-xs opacity-80 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 08 — Nachfassen */}
      <Accordion
        id="08"
        num="ABSCHNITT 08"
        icon="📬"
        title="Die Kunst des Nachfassens — Follow-Up"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-4">
          <p className="text-sm font-bold text-gray-800 mb-1">
            🕳️ Der "Schwarzes Loch"-Effekt
          </p>
          <p className="text-xs text-gray-600">
            Keine Antwort nach der Bewerbung? Das ist völlig normal — auch für
            Deutsche!
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-bold text-amber-800 mb-1">
              📞 Anruf — Gold Standard
            </p>
            <p className="text-xs text-amber-700">
              Zeigt Mut, Motivation & Deutschkenntnisse. Direkter, persönlicher
              Eindruck.
            </p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
            <p className="text-xs font-bold text-blue-800 mb-1">
              ✉️ E-Mail — Sicherer Weg
            </p>
            <p className="text-xs text-blue-700">
              Gut für den schriftlichen Nachweis. Keine Nervosität beim
              Sprechen.
            </p>
          </div>
        </div>

        <ScriptBlock
          title="Follow-Up Anruf — Skript"
          lines={[
            { text: '"Guten Tag, mein Name ist [Ihr Name].' },
            {
              text: 'Ich habe mich vor zwei Wochen bei Ihnen als [Name der Ausbildung] beworben. Ich wollte mich nur kurz erkundigen, ob meine Unterlagen gut bei Ihnen angekommen sind und ob Sie noch weitere Informationen von mir benötigen. Ich bin sehr an einer Ausbildung in Ihrem Unternehmen interessiert."',
              highlight: true,
            },
          ]}
        />

        <div className="mt-3">
          <ScriptBlock
            title="Follow-Up E-Mail — Vorlage"
            lines={[
              {
                label: "Betreff:",
                text: "Nachfrage zu meiner Bewerbung vom [Datum] – [Ihr Name]",
                highlight: true,
              },
              {
                text: '"Sehr geehrte Damen und Herren (oder Name des Ansprechpartners),',
              },
              {
                text: "am [Datum] habe ich mich bei Ihnen um einen Ausbildungsplatz als [Beruf] beworben. Da ich sehr an einer Mitarbeit in Ihrem Unternehmen interessiert bin, wollte ich mich kurz nach dem aktuellen Stand meiner Bewerbung erkundigen.",
              },
              {
                text: "Falls Sie noch Unterlagen benötigen, sende ich diese gerne sofort nach. Über eine kurze Rückmeldung würde ich mich sehr freuen.",
              },
              { text: 'Mit freundlichen Grüßen\n[Ihr Name]"' },
            ]}
          />
        </div>

        <InfoBox variant="tip" icon="⏱️" title="Die 2-Wochen-Regel">
          Warte <strong>14 Tage</strong> nach dem Absenden der Bewerbung. Wenn
          keine Antwort: Werde aktiv — erst E-Mail, dann Anruf.
        </InfoBox>
      </Accordion>

      {/* 09 — Mindset */}
      <Accordion
        id="09"
        num="ABSCHNITT 09"
        icon="🧠"
        title="Mindset — Qualität vor Quantität"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="bg-gray-900 rounded-xl p-4 mb-4 text-center">
          <p className="text-green-400 text-lg font-bold">
            Qualität schlägt Quantität — immer.
          </p>
          <p className="text-gray-400 text-xs mt-1">
            Ein deutscher Ausbilder merkt sofort, wenn du denselben Text an 50
            andere Firmen geschickt hast.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-bold text-red-800 mb-1">
              ⛔ Masse — funktioniert nicht
            </p>
            <p className="text-xs text-red-700">
              100 Copy-Paste-E-Mails bringen nichts. Personaler erkennen das
              sofort und löschen es.
            </p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-800 mb-1">
              ✅ Klasse — so geht es
            </p>
            <p className="text-xs text-green-700">
              10 maßgeschneiderte Bewerbungen sind besser als 100 generische.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-3">
          <p className="text-sm font-bold text-[#0369a1] mb-2">
            🏆 Die Erfolgsformel
          </p>
          <div className="flex items-center justify-center gap-2 text-sm font-bold text-[#0369a1]">
            <span className="bg-white px-3 py-1 rounded-lg border border-[#009fe3]/30">
              Top-Bewerbung
            </span>
            <span>+</span>
            <span className="bg-white px-3 py-1 rounded-lg border border-[#009fe3]/30">
              Follow-Up
            </span>
            <span>=</span>
            <span className="bg-[#009fe3] text-white px-3 py-1 rounded-lg">
              Ausbildungsplatz
            </span>
          </div>
          <div className="mt-3 space-y-1 text-xs text-[#0369a1]">
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 mt-0.5">›</span>
              <span>Investiere Zeit in die Recherche über die Firma</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 mt-0.5">›</span>
              <span>
                Zeige im Anschreiben, <em>warum</em> du genau zu <em>dieser</em>{" "}
                Firma willst
              </span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <p className="text-sm font-bold text-blue-800 mb-1">
            💪 Nicht entmutigen lassen!
          </p>
          <div className="space-y-1 text-xs text-blue-700">
            <p>Absagen sind normal — auch für Deutsche!</p>
            <p>
              Ein <strong className="text-red-600">"Nein"</strong> ist oft kein{" "}
              <strong className="text-orange-600">"Nie"</strong>, sondern ein{" "}
              <strong className="text-green-600">"Nicht jetzt"</strong>
            </p>
          </div>
        </div>
      </Accordion>

      {/* Fazit */}
      <Accordion
        id="fazit"
        num="FAZIT"
        icon="🏁"
        title="Fazit"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              Du kennst jetzt alle Suchstrategien — gezielte Suche schlägt
              Massenbewerbungen.
            </p>
            <ul className="space-y-1">
              {[
                "Make it in Germany + IHK — beste Portale für internationale Bewerber",
                "Ausbildung.de: Firmenkultur-Infos für Anschreiben nutzen",
                "Bundesagentur: KMUs sind Hidden Gems mit weniger Konkurrenz",
                "Gelbe Seiten + Google Maps — die 90%-Strategie die kaum jemand nutzt",
                "Keine Stellenanzeige? Vorabanfrage per Anruf oder E-Mail",
                "Portal-Sprung: Über Portale suchen, direkt bei Firmen bewerben",
                "14-Tage-Regel: Follow-up per E-Mail, dann per Telefon",
                "Qualität vor Quantität — 10 maßgeschneiderte > 100 generische",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Aufgaben */}
      <Accordion
        id="aufgaben"
        num="AUFGABEN"
        icon="📋"
        title="Deine Aufgaben — Lektion 08"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Fokus auf Qualität!</strong> — Lieber 2 maßgeschneiderte
                Bewerbungen als 10 generische
              </>,
              <>
                <strong>Update an G2G:</strong> Haltet uns auf dem Laufenden!
                Eingangsbestätigungen, Vorstellungsgespräche und auch Absagen
                melden
              </>,
              "Fokus auf B2-Sprachniveau sowie auf das Sprachzeugnis",
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default Lecture08AusbildungSuche;
